import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Circle, LogOut, Mic2, Square, Volume2 } from "lucide-react";
import { OnAirSign } from "../OnAirSign";
import { GoLiveDialog } from "./GoLiveDialog";

export const ctl = "flex items-center gap-2 rounded-[3px] px-3.5 py-2 text-sm font-semibold transition-colors";
export const mmss = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
};

export const useClock = (on) => {
  const [t, setT] = useState(Date.now());
  useEffect(() => {
    if (!on) return undefined;
    const i = setInterval(() => setT(Date.now()), 1000);
    return () => clearInterval(i);
  }, [on]);
  return t;
};

const subtitle = (kind, space) => {
  if (kind === "room") return `Stage room ${space.order}${space.genre ? ` · ${space.genre}` : ""}`;
  return `Lesson Room · ${space.lesson_type === "one_on_one" ? "One-on-one" : "Group"} with ${space.teacher_name}`;
};

const SoundButton = ({ engine }) => {
  const [on, setOn] = useState(engine.ctx.state === "running");
  if (on) return null;
  return (
    <button className={`${ctl} border border-[#39ff6a]/60 text-[#39ff6a]`} data-testid="enable-sound-btn"
      onClick={() => engine.ctx.resume().then(() => setOn(true))}><Volume2 className="h-4 w-4" /> Turn on sound</button>
  );
};

const StageButtons = ({ state, spaceId, post, refresh }) => {
  const { me } = state;
  return (
    <>
      {me.can_control && !me.on_stage && (
        <button className={`${ctl} bg-yellow-400 text-black hover:bg-yellow-300`} data-testid="take-stage-btn"
          onClick={() => post(`/rooms/${spaceId}/stage/join`)}><Mic2 className="h-4 w-4" /> Take the stage</button>
      )}
      {me.on_stage && (
        <button className={`${ctl} border border-zinc-700 hover:border-zinc-400`} data-testid="leave-stage-btn"
          onClick={() => post(`/rooms/${spaceId}/stage/leave`)}>Leave stage</button>
      )}
      {me.can_control && (state.on_air ? (
        <button className={`${ctl} border border-pink-500 text-pink-300 hover:bg-pink-500/15`} data-testid="end-show-btn"
          onClick={() => post(`/rooms/${spaceId}/live/stop`)}><Square className="h-4 w-4" /> End show</button>
      ) : <GoLiveDialog roomId={spaceId} me={me} onLive={refresh} />)}
    </>
  );
};

const RecordButton = ({ rec, now }) => (rec.recording ? (
  <button className={`${ctl} bg-pink-500 text-white`} data-testid="record-stop-btn" onClick={rec.stop}>
    <Square className="h-4 w-4" /> Stop · {mmss(now - rec.startedAt)}
  </button>
) : (
  <button className={`${ctl} border border-zinc-700 hover:border-pink-400`} data-testid="record-start-btn" disabled={rec.uploading} onClick={rec.start}>
    <Circle className="h-3.5 w-3.5 fill-pink-500 text-pink-500" /> {rec.uploading ? "Saving…" : "Record"}
  </button>
));

export const SpaceHeader = ({ state, spaceId, name, engine, rec, now, post, refresh }) => {
  const navigate = useNavigate();
  const { me, kind, space, show } = state;
  const endLesson = async () => { await post(`/lessons/${spaceId}/end`); navigate("/lesson"); };
  return (
    <div className="flex flex-wrap items-center gap-4">
      <div className="min-w-0">
        <span className="mono-label">{subtitle(kind, space)}</span>
        <h1 className="text-3xl md:text-4xl font-black tracking-tight truncate" data-testid="space-title">{name}</h1>
      </div>
      {kind === "room" && <OnAirSign on={state.on_air} />}
      {show && <span className="mono text-sm text-pink-300" data-testid="show-countdown">{show.title} · {mmss(Date.parse(show.ends_at) - now)} left</span>}
      <div className="ml-auto flex flex-wrap gap-2">
        <SoundButton engine={engine} />
        {kind === "room" && <StageButtons state={state} spaceId={spaceId} post={post} refresh={refresh} />}
        {me.can_control && <RecordButton rec={rec} now={now} />}
        {kind === "lesson" && me.can_control && (
          <button className={`${ctl} border border-zinc-700 hover:border-pink-400`} data-testid="end-lesson-btn" onClick={endLesson}>
            <LogOut className="h-4 w-4" /> End lesson
          </button>
        )}
      </div>
    </div>
  );
};

export const InviteBanner = ({ spaceId, post }) => (
  <div className="mt-4 flex flex-wrap items-center gap-3 rounded-md border border-pink-500/60 bg-pink-500/10 px-4 py-3 glow-pink" data-testid="stage-invite-banner">
    <span className="font-semibold text-pink-200">You're invited up to the stage.</span>
    <button className={`${ctl} bg-pink-500 text-white`} data-testid="accept-stage-invite-btn" onClick={() => post(`/rooms/${spaceId}/stage/accept`)}>Go on stage</button>
    <button className="text-sm text-zinc-400 hover:text-white" data-testid="decline-stage-invite-btn" onClick={() => post(`/rooms/${spaceId}/stage/leave`)}>Not now</button>
  </div>
);
